
import { supabase } from '@/integrations/supabase/client';

/**
 * Validates a feedback token and returns information about its validity
 * @param token The token to validate
 * @param feedbackId Optional feedbackId to verify specific access
 * @returns Object containing validation results
 */
export const validateFeedbackToken = async (token: string, feedbackId?: string) => {
  try {
    console.log("Validating token:", token);
    
    // Verify token validity
    const { data, error } = await supabase
      .from('feedback_tokens')
      .select('*, feedback_requests(*), candidate_to_recruiter_feedback_requests(*)')
      .eq('token', token)
      .maybeSingle();
      
    if (error || !data) {
      console.error("Token validation error:", error);
      return { isValid: false, error: 'Token invalide ou expiré' };
    }
    
    console.log("Token data found:", data);
    
    // Check if the token has already been used
    if (data.used) {
      console.log("Token already used");
      return { isValid: false, error: 'Ce lien a déjà été utilisé' };
    }
    
    // Check if the token has expired
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      console.log("Token expired");
      return { isValid: false, error: 'Ce lien a expiré' };
    }
    
    // Get the request data either from feedback_requests or candidate_to_recruiter_feedback_requests
    let requestData = null;
    let tableToUse = null;
    
    if (data.candidate_to_recruiter_feedback_requests) {
      console.log("Found data in candidate_to_recruiter_feedback_requests");
      requestData = data.candidate_to_recruiter_feedback_requests;
      tableToUse = 'candidate_to_recruiter_feedback_requests';
    } else if (data.feedback_requests) {
      console.log("Found data in feedback_requests");
      requestData = data.feedback_requests;
      tableToUse = 'feedback_requests';
    }
    
    if (!requestData) {
      console.error("No request data found for token:", token);
      return { isValid: false, error: 'Demande associée non trouvée' };
    }
    
    console.log("Request data found:", requestData);
    console.log("Table to use:", tableToUse);
    
    // Initialize variables to store the feedback data
    let feedbackDataId: string | null = null;
    let validFeedbackData: any = null;
    
    // Token is valid
    return { 
      isValid: true, 
      feedbackId: feedbackDataId,
      requestId: data.request_id,
      requestData: requestData,
      feedbackData: validFeedbackData,
      tableToUse: tableToUse
    };
  } catch (error) {
    console.error('Erreur lors de la validation du token:', error);
    return { isValid: false, error: 'Erreur lors de la validation du token' };
  }
};

/**
 * Marks a feedback token as used
 * @param token The token to mark as used
 */
export const markTokenAsUsed = async (token: string) => {
  try {
    console.log("Marking token as used:", token);
    
    const { data, error } = await supabase
      .from('feedback_tokens')
      .update({ used: true, used_at: new Date().toISOString() })
      .eq('token', token)
      .select();
      
    if (error) {
      console.error('Erreur lors du marquage du token comme utilisé:', error);
      return false;
    }
    
    console.log("Token successfully marked as used:", data);
    return true;
  } catch (error) {
    console.error('Erreur lors du marquage du token comme utilisé:', error);
    return false;
  }
};
